import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { EqlFieldName } from '@modules/common/constants';
import { getFieldNameFromEqlField } from '@modules/common/utils';
import { getDisplayNameFromField } from '@modules/common/utils/getDisplayNameFromField';
import { CooccurrenceIcon } from '@modules/quantify/components/CooccurrenceIcon';
import {
  useFetchQuantifyReport,
  useShouldShowQuantifyCooccurringToggle,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { InfoOutlined } from '@mui/icons-material';
import {
  alpha,
  Box,
  Divider,
  FormControlLabel,
  FormControlLabelProps,
  Stack,
  Switch,
} from '@mui/material';
import pluralize from 'pluralize';
import { ChangeEvent, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { shallow } from 'zustand/shallow';
import { QuantifyCooccurrenceToggleTooltip } from '../QuantifyCooccurrenceToggleTooltip';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');

interface QuantifyCooccurrenceToggleProps {
  components?: {
    FormControlLabel?: Omit<FormControlLabelProps, 'control' | 'label'>;
  };
}

export const QuantifyCooccurrenceToggle = ({
  components,
}: QuantifyCooccurrenceToggleProps) => {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const shouldShowCooccurringDataPoints = useQuantifyStore(
    ({ metadata }) => metadata.shouldShowCooccurringDataPoints,
  );

  const { title: name, href } = useAnalysisStore(
    ({ title, href, scope }) => ({ title, href, scope }),
    shallow,
  );

  const fetchQuantifyReport = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const shouldShowCoocurringToggle = useShouldShowQuantifyCooccurringToggle();

  useUpdateEffect(() => {
    if (shouldFetchReportData) {
      setShouldFetchReportData(false);
      fetchQuantifyReport();
    }
  }, [shouldFetchReportData]);

  if (!shouldShowCoocurringToggle) {
    return null;
  }

  if (
    groupBy?.field !== EqlFieldName.Entity &&
    groupBy?.field !== EqlFieldName.Reason
  ) {
    return null;
  }

  const onChangeToggle = (
    _: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    dispatch({
      type: QuantifyActionType.SET_METADATA,
      payload: {
        shouldShowCooccurringDataPoints: checked,
      },
    });
    setShouldFetchReportData(true);
    trackQuantifyReportEvents({
      eventName: `Quantify Report - Show Co-occurring Toggled ${
        checked ? 'On' : 'Off'
      }`,
      name,
      link: href,
      query: {
        showMeLabel: groupBy.displayName,
      },
    });
  };

  const renderSwitchLabel = () => {
    return (
      <>
        <CooccurrenceIcon />
        Include co-occurring{' '}
        {groupBy
          ? pluralize(
              getDisplayNameFromField(getFieldNameFromEqlField(groupBy)),
            )
          : ''}
      </>
    );
  };

  return (
    <Stack alignItems="center" width="fit-content" direction="row">
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={shouldShowCooccurringDataPoints}
              onChange={onChangeToggle}
              size="small"
            />
          }
          label={renderSwitchLabel()}
          componentsProps={{
            typography: {
              variant: 'body2',
            },
          }}
          labelPlacement="start"
          sx={{
            mr: 0,
            ...components?.FormControlLabel?.sx,
          }}
        />
      </Box>

      <Divider
        variant="middle"
        sx={{ height: '24px !important', mx: 1 }}
        orientation="vertical"
      />
      <QuantifyCooccurrenceToggleTooltip>
        <InfoOutlined
          sx={{
            color: (theme) => alpha(theme.palette.action.active, 0.56),
          }}
        />
      </QuantifyCooccurrenceToggleTooltip>
    </Stack>
  );
};
